<template>
  <div
    class="flex flex-col w-full h-full items-center justify-center text-center"
  >
    <!-- title -->
    <div class="font-bold w-full flex">
      {{ $t("usersManagement.csv") }}
    </div>

    <!-- Intro -->
    <div class="flex flex-row w-full">
      <div
        class="mt-5 w-auto flex text-left"
        v-html="$t('usersManagement.csvIntro')"
      />

      <!-- Info icon -->
      <div class="mt-4 ml-2">
        <rele-info-tooltip
          :tooltipStyle="{ right: '40%' }"
          icon="info"
          :tooltip-html="$t('usersManagement.csvTooltip')"
        />
      </div>
    </div>

    <!-- Upload csv container -->
    <div
      class="mt-5 p-12 md:py-32 py-20 border-dashed border-2 w-full"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <!-- Input file -->
      <input
        type="file"
        multiple
        name="fields[assetsFieldHandle][]"
        id="assetsFieldHandle"
        class="w-px h-px opacity-0 overflow-hidden absolute"
        @change="onFileListChange"
        ref="file"
        accept=".csv"
      />

      <!-- Upload Info -->
      <label for="assetsFieldHandle" class="block cursor-pointer">
        <font-awesome-icon
          icon="file-csv"
          color="grey"
          size="3x"
          class="mb-4"
        />
        <div class="text-gray-500" v-html="$t('forms.csvInfo')" />
      </label>

      <!-- Files list -->
      <div class="flex flex-row justify-center">
        <div class="text-sm p-1" v-for="(file, i) in filelist" :key="i">
          <div class="flex flex-row w-full justify-center p-2">
            <!-- remove file icon -->
            <font-awesome-icon
              icon="minus"
              color="grey"
              class="mr-2 mt-1 cursor-pointer"
              @click="remove(filelist.indexOf(file))"
            />

            <!-- file name -->
            <div class="text-gray-700">{{ file.name }}</div>
          </div>
        </div>
      </div>
    </div>
    <!-- Buttons -->
    <div class="flex flex-row w-full justify-end mt-5">
      <!-- cancel -->
      <r-button
        id="user-csv-cancel"
        class="mr-4"
        text
        color="black"
        @click="onCloseCsv"
      >
        {{ $t("buttons.cancel") }}
      </r-button>

      <!-- done -->
      <r-button
        id="user-csv-save"
        :loading="loading"
        @click="onSaveCsv"
        :disabled="isFormDisable"
      >
        {{ $t("buttons.save") }}
      </r-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

export default {
  // Avoid Twig conflicts
  delimiters: ["${", "}"],

  // define state
  data() {
    return {
      // filelist array
      filelist: [],

      // loading flag
      loading: false
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // computed props
  computed: {
    ...mapGetters({
      // get user doc
      userDoc: "users/getDoc"
    }),

    // check if form should be disable
    isFormDisable() {
      return Boolean(!this.filelist.length)
    },

    /**
     * Get selected org
     */
    selectedOrg() {
      const { orgs = [] } = this.userDoc
      return orgs.length ? orgs[0] : ""
    }
  },

  // local methods
  methods: {
    /**
     * On file list change
     */
    onFileListChange() {
      this.filelist = [...this.$refs.file.files]
    },

    /**
     * Remove file from list
     */
    remove(i) {
      this.filelist.splice(i, 1)
    },

    /**
     * on drag file to destination layout
     */
    dragover(event) {
      // prevent event default
      event.preventDefault()

      // Add some visual fluff to show the user can drop its files
      event.currentTarget.classList.remove("bg-white")
      event.currentTarget.classList.add("bg-gray-200")
    },

    /**
     * on drag leave file to destination layout
     */
    dragleave(event) {
      // Clean up
      event.currentTarget.classList.add("bg-white")
      event.currentTarget.classList.remove("bg-gray-200")
    },

    /**
     * on drop file to destination payload
     */
    drop(event) {
      try {
        // prevent event default
        event.preventDefault()

        // validate file type to csv
        Array.from(event.dataTransfer.files).forEach((file) => {
          if (file.type !== "text/csv") {
            throw new Error("unsupported file type. file type should be csv.")
          }
        })

        // set file list
        this.$refs.file.files = event.dataTransfer.files
        this.onFileListChange()
      } catch (error) {
        console.error(error)
        this.notifyError({
          title: this.$t("errors.unableToDropFile"),
          message: error.message || "unable to drop file."
        })
      }

      // Clean up
      event.currentTarget.classList.add("bg-white")
      event.currentTarget.classList.remove("bg-gray-200")
    },

    /**
     * onCloseCsv fired on close dialog
     */
    onCloseCsv() {
      this.$emit("close-csv")
    },

    /**
     * onSaveCsv upload csv
     */
    async onSaveCsv() {
      try {
        // set loading
        this.loading = true

        const botLangs = {
          EN: 1,
          ES: 2,
          FR: 3,
          GR: 4,
          INHERIT: 5
        }

        // load csv files
        const csvs = await this.loadCsvs()

        // convert csv to jsons
        const users = this.csvsToJsons(csvs)

        // create users
        const userPrms = users.map(async (user) => {
          const data = {
            botLang: botLangs[(user.botLang || "").toUpperCase()] || 5,
            lastName: user.lastName || "",
            firstName: user.firstName,
            mobiles: [(user.mobile || "").replace(/([+|-])/g, "")],
            emails: [user.email || ""],
            orgs: [this.selectedOrg],
            mobiles_country_code: user.countryCode || "US"
          }

          await this.$store.dispatch("users/add", data)
        })

        // resolve promises
        await Promise.all(userPrms)
      } catch (error) {
        console.error(error)
        this.notifyError({
          title: this.$t("errors.unableToCreateUser"),
          message: error.message || "unable to create users using CSV"
        })
      }

      // set loading to false
      this.loading = false

      // close dialog
      this.onCloseCsv()
    },

    /**
     * Load CSV files
     */
    async loadCsvs() {
      const prms = this.filelist.map(async (file) => {
        // format each file
        return new Promise((resolve, reject) => {
          // define file reader object
          const fr = new FileReader()

          // on load handler - fired when file loaded
          fr.onload = () => {
            resolve(fr.result.split("\n"))
          }

          // on error
          fr.onerror = () => {
            reject(new Error("unable to load csv files"))
          }

          // read file as text
          fr.readAsText(file)
        })
      })

      // resolve promises
      return await Promise.all(prms)
    },

    /**
     * csvsToJsons takes csv matrix and convert
     * it to users lisr
     */
    csvsToJsons(csvs) {
      // define users matrix
      let users = []

      // loop over csvs
      for (let csv of csvs) {
        const result = []
        const headers = csv[0].split(",")

        // build object
        for (let i = 1; i < csv.length; i++) {
          if (!csv[i]) continue
          const obj = {}
          const currentline = csv[i].split(",")

          // add to json
          for (let j = 0; j < headers.length; j++) {
            obj[headers[j]] = currentline[j]
          }

          // push to result list
          result.push(obj)
        }

        // push to users matrix
        users.push(result)
      }

      // return list of users
      return users.flat()
    }
  }
}
</script>
